import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import clsx from 'clsx';
import { sortBy } from 'lodash';

import { Button } from '../../Elements/Button';
import { Skeleton } from '../../Elements/Skeletons';

export type MessageType = 'SMS_REQUEST' | 'MMS_REQUEST';

export interface ConversationMessage {
  id: string;
  type?: MessageType;
  insertedAt: string | Date;
  direction: string | null;
  displayBody: string | null;
  status: string | null;
  multipartId?: string | null;
  udhCount?: number | null;
  udhSeqNum?: number | null;
}

interface ProcessedMessage {
  id: string;
  type?: MessageType;
  insertedAt: string | Date;
  direction: string | null;
  displayBody: string;
  nodeStatus: string;
  segments: ConversationMessage[];
}

export interface ConversationThreadProps {
  messages: ConversationMessage[];
  loading?: boolean;
  error?: boolean;
  hostNumber?: string | null;
  remoteNumber?: string | null;
  /**
   * Id of the message whose bubble should be visually highlighted (e.g. the
   * SMS the user is currently viewing the detail page for). Matches the bubble
   * if either the bubble's id or any of its segment ids equals this value.
   */
  highlightedMessageId?: string | null;
  /** Build a link target for a message bubble. If omitted, bubbles are not links. */
  getMessageHref?: (message: ProcessedMessage) => string | undefined;

  // Bidirectional pagination
  hasMoreOlder?: boolean;
  hasMoreNewer?: boolean;
  loadingOlder?: boolean;
  loadingNewer?: boolean;
  onLoadOlder?: () => void;
  onLoadNewer?: () => void;
}

const SCROLL_TRIGGER_PX = 200;

const buildStream = (nodes: ConversationMessage[]): ProcessedMessage[] => {
  const multipart: Record<string, ConversationMessage[]> = {};
  const items: ProcessedMessage[] = [];

  for (const node of nodes) {
    const isMultipart = !!node.multipartId && !!node.udhCount && (node.udhCount ?? 0) > 1;
    if (isMultipart) {
      const key = node.multipartId as string;
      (multipart[key] ??= []).push(node);
    } else {
      items.push({
        id: node.id,
        type: node.type,
        insertedAt: node.insertedAt,
        direction: node.direction,
        displayBody: node.displayBody ?? '',
        nodeStatus: node.status ?? '',
        segments: [node],
      });
    }
  }

  for (const key of Object.keys(multipart)) {
    const parts = sortBy(multipart[key], (a) => a.udhSeqNum ?? 0);
    const first = parts[0];
    let status = first.status ?? '';
    for (const p of parts) {
      if ((p.status ?? '') !== status) status = 'MIXED';
    }
    items.push({
      id: first.multipartId ?? first.id,
      type: first.type,
      insertedAt: first.insertedAt,
      direction: first.direction,
      displayBody: parts.map((p) => p.displayBody ?? '').join(''),
      nodeStatus: status,
      segments: parts,
    });
  }

  return sortBy(items, (a) => new Date(a.insertedAt).getTime());
};

type StreamItem =
  | { kind: 'date'; date: string }
  | { kind: 'message'; message: ProcessedMessage };

const withDateSeparators = (items: ProcessedMessage[]): StreamItem[] => {
  const out: StreamItem[] = [];
  let lastDate: string | null = null;
  for (const m of items) {
    const date = new Date(m.insertedAt).toISOString().slice(0, 10);
    if (date !== lastDate) {
      out.push({ kind: 'date', date });
      lastDate = date;
    }
    out.push({ kind: 'message', message: m });
  }
  return out;
};

export const ConversationThread = ({
  messages,
  loading,
  error,
  hostNumber,
  remoteNumber,
  highlightedMessageId,
  getMessageHref,
  hasMoreOlder,
  hasMoreNewer,
  loadingOlder,
  loadingNewer,
  onLoadOlder,
  onLoadNewer,
}: ConversationThreadProps) => {
  const highlightedRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const didInitialScroll = useRef(false);

  // For scroll preservation when older messages prepend.
  const prevFirstId = useRef<string | null>(null);
  const prevScrollHeight = useRef(0);
  const prevScrollTop = useRef(0);

  const isHighlighted = (m: ProcessedMessage) =>
    !!highlightedMessageId &&
    (m.id === highlightedMessageId ||
      m.segments.some((s) => s.id === highlightedMessageId));

  const scrollToHighlighted = () => {
    highlightedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // Auto-scroll to highlighted bubble once on initial load.
  useEffect(() => {
    if (didInitialScroll.current) return;
    if (loading || !highlightedMessageId) return;
    const node = highlightedRef.current;
    if (!node) return;
    didInitialScroll.current = true;
    const handle = window.requestAnimationFrame(() => {
      node.scrollIntoView({ behavior: 'auto', block: 'center' });
    });
    return () => window.cancelAnimationFrame(handle);
  }, [loading, highlightedMessageId, messages.length]);

  // Preserve scroll position when older messages prepend.
  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const firstId = messages[0]?.id ?? null;
    const prependedOlder =
      prevFirstId.current !== null &&
      firstId !== null &&
      firstId !== prevFirstId.current &&
      messages.some((m) => m.id === prevFirstId.current);

    if (prependedOlder) {
      const newScrollHeight = el.scrollHeight;
      el.scrollTop = prevScrollTop.current + (newScrollHeight - prevScrollHeight.current);
    }

    prevFirstId.current = firstId;
    prevScrollHeight.current = el.scrollHeight;
    prevScrollTop.current = el.scrollTop;
  }, [messages]);

  // Edge-triggered auto pagination.
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    if (el.scrollTop < SCROLL_TRIGGER_PX && hasMoreOlder && !loadingOlder) {
      onLoadOlder?.();
    }
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < SCROLL_TRIGGER_PX && hasMoreNewer && !loadingNewer) {
      onLoadNewer?.();
    }
  }, [hasMoreOlder, hasMoreNewer, loadingOlder, loadingNewer, onLoadOlder, onLoadNewer]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center py-12 text-grey-600 dark:text-grey-400">
        <span className="text-4xl material-symbols-outlined">error</span>
        <p>Unable to load conversation.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-3 p-5 border rounded-2xl border-grey-400 dark:border-grey-700 bg-grey-100 dark:bg-blue-1000">
        <Skeleton count={4} height="60px" />
      </div>
    );
  }

  if (!messages.length) {
    return (
      <div className="flex justify-center items-center py-12 rounded-2xl border border-grey-400 dark:border-grey-700 bg-grey-100 dark:bg-blue-1000">
        <p className="text-grey-600 dark:text-grey-400">No messages in this conversation.</p>
      </div>
    );
  }

  const stream = withDateSeparators(buildStream(messages));

  const edgeIndicator = (
    isLoading: boolean | undefined,
    hasMore: boolean | undefined,
    edge: 'older' | 'newer'
  ) => {
    if (isLoading) {
      return (
        <div className="py-3 text-sm text-center text-grey-600 dark:text-grey-400">
          {edge === 'older' ? 'Loading older messages…' : 'Loading newer messages…'}
        </div>
      );
    }
    if (hasMore === false) {
      return (
        <div className="py-3 text-xs uppercase tracking-wide text-center text-grey-500 dark:text-grey-500">
          {edge === 'older' ? 'Beginning of conversation' : 'End of conversation'}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="p-5 border rounded-2xl border-grey-400 dark:border-grey-700 bg-grey-100 dark:bg-blue-1000">
      {(hostNumber || remoteNumber || highlightedMessageId) && (
        <div className="flex flex-row flex-wrap gap-3 justify-between items-center mb-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:gap-8">
            {remoteNumber && (
              <div>
                <div className="text-sm text-grey-600 dark:text-grey-400">Remote Number</div>
                <div className="font-mono">{remoteNumber}</div>
              </div>
            )}
            {hostNumber && (
              <div>
                <div className="text-sm text-grey-600 dark:text-grey-400">Host Number</div>
                <div className="font-mono">{hostNumber}</div>
              </div>
            )}
          </div>
          {highlightedMessageId && (
            <Button size="sm" variant="secondary" onClick={scrollToHighlighted}>
              Return to message
            </Button>
          )}
        </div>
      )}

      <div
        ref={scrollContainerRef}
        className="flex flex-col gap-5 overflow-y-auto max-h-[60vh] p-4 rounded-xl border border-grey-400 dark:border-grey-700"
      >
        {edgeIndicator(loadingOlder, hasMoreOlder, 'older')}
        {stream.map((item, index) => {
          if (item.kind === 'date') {
            return (
              <div
                key={`date-${item.date}-${index}`}
                className="w-full pb-2 text-center border-b border-grey-400 dark:border-grey-700 text-sm text-grey-600 dark:text-grey-400"
              >
                {new Date(item.date).toLocaleDateString()}
              </div>
            );
          }
          const m = item.message;
          const inbound = m.direction === 'INBOUND';
          const highlighted = isHighlighted(m);
          const href = getMessageHref?.(m);
          const Body = href ? (
            <a
              href={href}
              className="block transition-colors hover:bg-white/40 rounded"
            >
              {m.displayBody}
            </a>
          ) : (
            <span>{m.displayBody}</span>
          );
          return (
            <div
              key={m.id}
              className={clsx('flex flex-row', inbound ? 'justify-start' : 'justify-end')}
            >
              <div
                className={clsx(
                  'flex flex-col gap-1 max-w-3xl',
                  inbound ? 'items-start' : 'items-end'
                )}
              >
                <div className="flex gap-2 items-center text-xs uppercase text-grey-600 dark:text-grey-400">
                  {highlighted && (
                    <span className="px-1.5 py-0.5 rounded font-semibold text-blue-700 bg-blue-200 dark:text-blue-200 dark:bg-blue-800">
                      Current
                    </span>
                  )}
                  {m.type && <span>{m.type === 'MMS_REQUEST' ? 'MMS' : 'SMS'}</span>}
                </div>
                <div
                  ref={highlighted ? highlightedRef : undefined}
                  className={clsx(
                    'p-4 rounded-2xl scroll-mt-24',
                    inbound
                      ? 'bg-amber-100 text-grey-1000 rounded-bl-sm'
                      : 'bg-blue-200/70 dark:bg-blue-200 text-grey-1000 rounded-br-sm',
                    highlighted && 'ring-2 ring-blue-500 dark:ring-blue-400 ring-offset-2 ring-offset-grey-100 dark:ring-offset-blue-1000'
                  )}
                >
                  {Body}
                </div>
                <div className="text-xs text-grey-600 dark:text-grey-400">
                  {new Date(m.insertedAt).toLocaleTimeString()}
                  {m.nodeStatus ? ` · ${m.nodeStatus}` : ''}
                </div>
              </div>
            </div>
          );
        })}
        {edgeIndicator(loadingNewer, hasMoreNewer, 'newer')}
      </div>
    </div>
  );
};
